import { BaseAPIRequestFactory, RequiredError } from './baseapi';
import type { Configuration } from '../configuration';
import { RequestContext, HttpMethod, ResponseContext, HttpInfo } from '../http/http';
import { ObjectSerializer } from '../models/ObjectSerializer';
import { ApiException } from './exception';
import { isCodeInRange } from '../util';
import type {SecurityAuthentication} from '../auth/auth';
import type {HttpFile} from '../http/http';


import { TakeNativeSnapshotRequest } from '../models/TakeNativeSnapshotRequest';
import { TakeSnapshotRequest } from '../models/TakeSnapshotRequest';
import { TakeSnapshotResponse } from '../models/TakeSnapshotResponse';

/**
 * no description
 */
export class BackupsApiRequestFactory extends BaseAPIRequestFactory {

  /**
   * Take LLG Snapshot
   * @param id Eon-assigned ID of the llg resource to back up.
   * @param projectId ID of the project the resource is in. You can get your project ID from the [API Credentials](https://console.eon.io/global-settings/api-credentials) page in your global settings. 
   * @param takeSnapshotRequest 
   */
  public async takeLlgSnapshot(id: string, projectId: string, takeSnapshotRequest: TakeSnapshotRequest, _options?: Configuration): Promise<RequestContext> {
    let _config;
    if (!_options) {
      _config = this.configuration;
    } else {
      _config = Object.assign({}, _options, this.configuration);
    }
    


    // verify required parameter 'id' is not null or undefined
    if (id === null || id === undefined) {
      throw new RequiredError("BackupsApi", "takeLlgSnapshot", "id");
    }


    // verify required parameter 'projectId' is not null or undefined
    if (projectId === null || projectId === undefined) {
      throw new RequiredError("BackupsApi", "takeLlgSnapshot", "projectId");
    }


    // verify required parameter 'takeSnapshotRequest' is not null or undefined
    if (takeSnapshotRequest === null || takeSnapshotRequest === undefined) {
      throw new RequiredError("BackupsApi", "takeLlgSnapshot", "takeSnapshotRequest");
    }


    // Path Params
    const localVarPath = '/projects/{projectId}/resources/{id}/take-llg-snapshot'
      .replace('{' + 'id' + '}', encodeURIComponent(String(id)))
      .replace('{' + 'projectId' + '}', encodeURIComponent(String(projectId)));

    let signal = _config.timeout ? AbortSignal.timeout(_config.timeout) : undefined;
    // Make Request Context
    const requestContext = _config.baseServer!.makeRequestContext(localVarPath, HttpMethod.POST, signal);
    requestContext.setHeaderParam("Accept", "application/json, */*;q=0.8")


    // Body Params
    const contentType = "application/json"
    requestContext.setHeaderParam("Content-Type", contentType);
    const serializedBody = JSON.stringify(ObjectSerializer.serialize(takeSnapshotRequest, "TakeSnapshotRequest", ""));
    requestContext.setBody(serializedBody);

    let authMethod: SecurityAuthentication | undefined;
    // Apply auth methods
    authMethod = _config.authMethods!["ApiKeyAuth"]
    if (authMethod?.applySecurityAuthentication) {
      await authMethod?.applySecurityAuthentication(requestContext);
    }

    return requestContext;
  }

  /**
   * Take Native Snapshot
   * @param id Eon-assigned ID of the resource to back up.
   * @param projectId ID of the project the resource is in. You can get your project ID from the [API Credentials](https://console.eon.io/global-settings/api-credentials) page in your global settings. 
   * @param takeNativeSnapshotRequest 
   */
  public async takeNativeSnapshot(id: string, projectId: string, takeNativeSnapshotRequest: TakeNativeSnapshotRequest, _options?: Configuration): Promise<RequestContext> {
    let _config;
    if (!_options) {
      _config = this.configuration;
    } else {
      _config = Object.assign({}, _options, this.configuration);
    }
    


    // verify required parameter 'id' is not null or undefined
    if (id === null || id === undefined) {
      throw new RequiredError("BackupsApi", "takeNativeSnapshot", "id");
    }


    // verify required parameter 'projectId' is not null or undefined
    if (projectId === null || projectId === undefined) {
      throw new RequiredError("BackupsApi", "takeNativeSnapshot", "projectId");
    }


    // verify required parameter 'takeNativeSnapshotRequest' is not null or undefined
    if (takeNativeSnapshotRequest === null || takeNativeSnapshotRequest === undefined) {
      throw new RequiredError("BackupsApi", "takeNativeSnapshot", "takeNativeSnapshotRequest");
    }


    // Path Params
    const localVarPath = '/v1/projects/{projectId}/resources/{id}/take-native-snapshot'
      .replace('{' + 'id' + '}', encodeURIComponent(String(id)))
      .replace('{' + 'projectId' + '}', encodeURIComponent(String(projectId)));

    let signal = _config.timeout ? AbortSignal.timeout(_config.timeout) : undefined;
    // Make Request Context
    const requestContext = _config.baseServer!.makeRequestContext(localVarPath, HttpMethod.POST, signal);
    requestContext.setHeaderParam("Accept", "application/json, */*;q=0.8")


    // Body Params
    const contentType = "application/json"
    requestContext.setHeaderParam("Content-Type", contentType);
    const serializedBody = JSON.stringify(ObjectSerializer.serialize(takeNativeSnapshotRequest, "TakeNativeSnapshotRequest", ""));
    requestContext.setBody(serializedBody);

    let authMethod: SecurityAuthentication | undefined;
    // Apply auth methods
    authMethod = _config.authMethods!["ApiKeyAuth"]
    if (authMethod?.applySecurityAuthentication) {
      await authMethod?.applySecurityAuthentication(requestContext);
    }

    return requestContext;
  }

  /**
   * Take Snapshot
   * @param id Eon-assigned ID of the resource to back up.
   * @param projectId ID of the project the resource is in. You can get your project ID from the [API Credentials](https://console.eon.io/global-settings/api-credentials) page in your global settings. 
   * @param takeSnapshotRequest 
   */
  public async takeSnapshot(id: string, projectId: string, takeSnapshotRequest: TakeSnapshotRequest, _options?: Configuration): Promise<RequestContext> {
    let _config;
    if (!_options) {
      _config = this.configuration;
    } else {
      _config = Object.assign({}, _options, this.configuration);
    }
    


    // verify required parameter 'id' is not null or undefined
    if (id === null || id === undefined) {
      throw new RequiredError("BackupsApi", "takeSnapshot", "id");
    }


    // verify required parameter 'projectId' is not null or undefined
    if (projectId === null || projectId === undefined) {
      throw new RequiredError("BackupsApi", "takeSnapshot", "projectId");
    }


    // verify required parameter 'takeSnapshotRequest' is not null or undefined
    if (takeSnapshotRequest === null || takeSnapshotRequest === undefined) {
      throw new RequiredError("BackupsApi", "takeSnapshot", "takeSnapshotRequest");
    }


    // Path Params
    const localVarPath = '/v1/projects/{projectId}/resources/{id}/take-snapshot'
      .replace('{' + 'id' + '}', encodeURIComponent(String(id)))
      .replace('{' + 'projectId' + '}', encodeURIComponent(String(projectId)));

    let signal = _config.timeout ? AbortSignal.timeout(_config.timeout) : undefined;
    // Make Request Context
    const requestContext = _config.baseServer!.makeRequestContext(localVarPath, HttpMethod.POST, signal);
    requestContext.setHeaderParam("Accept", "application/json, */*;q=0.8")


    // Body Params
    const contentType = "application/json"
    requestContext.setHeaderParam("Content-Type", contentType);
    const serializedBody = JSON.stringify(ObjectSerializer.serialize(takeSnapshotRequest, "TakeSnapshotRequest", ""));
    requestContext.setBody(serializedBody);

    let authMethod: SecurityAuthentication | undefined;
    // Apply auth methods
    authMethod = _config.authMethods!["ApiKeyAuth"]
    if (authMethod?.applySecurityAuthentication) {
      await authMethod?.applySecurityAuthentication(requestContext);
    }

    return requestContext;
  }

}

export class BackupsApiResponseProcessor {

  /**
   * Unwraps the actual response sent by the server from the response context and deserializes the response content
   * to the expected objects
   *
   * @params response Response returned by the server for a request to takeLlgSnapshot
   * @throws ApiException if the response code was not in [200, 299]
   */
   public async takeLlgSnapshotWithHttpInfo(response: ResponseContext): Promise<HttpInfo<TakeSnapshotResponse >> {
    if (isCodeInRange("202", response.httpStatusCode)) {
      const body: TakeSnapshotResponse = ObjectSerializer.deserialize(
        await response.body.json(),
        "TakeSnapshotResponse", ""
      ) as TakeSnapshotResponse;
      return new HttpInfo(response.httpStatusCode, response.headers, response.body, body);
    }
    if (isCodeInRange("429", response.httpStatusCode)) {
      throw new ApiException<undefined>(response.httpStatusCode, "Limit exceeded for the number of simultaneous on-demand backup jobs.", undefined, response.headers, response);
    }
    if (isCodeInRange("1XX", response.httpStatusCode)) {
      throw new ApiException<undefined>(response.httpStatusCode, "Informational", undefined, response.headers, response);
    }
    if (isCodeInRange("3XX", response.httpStatusCode)) {
      throw new ApiException<undefined>(response.httpStatusCode, "Redirect", undefined, response.headers, response);
    }
    if (isCodeInRange("4XX", response.httpStatusCode)) {
      const body: Error = ObjectSerializer.deserialize(
        await response.body.json(),
        "Error", ""
      ) as Error;
      throw new ApiException<Error>(response.httpStatusCode, "Client Error", body, response.headers, response);
    }
    if (isCodeInRange("5XX", response.httpStatusCode)) {
      const body: Error = ObjectSerializer.deserialize(
        await response.body.json(),
        "Error", ""
      ) as Error;
      throw new ApiException<Error>(response.httpStatusCode, "Internal Server Error", body, response.headers, response);
    }

    // Work around for missing responses in specification, e.g. for petstore.yaml
    if (response.httpStatusCode < 200 || response.httpStatusCode > 299) {
      throw new ApiException<string | Blob | undefined>(response.httpStatusCode, "Unknown API Status Code!", await response.getBodyAsAny(), response.headers, response);
    }
    const body: TakeSnapshotResponse = await response.body.json() as TakeSnapshotResponse;
    return new HttpInfo(response.httpStatusCode, response.headers, response.body, body);
  }

  /**
   * Unwraps the actual response sent by the server from the response context and deserializes the response content
   * to the expected objects
   *
   * @params response Response returned by the server for a request to takeNativeSnapshot
   * @throws ApiException if the response code was not in [200, 299]
   */
   public async takeNativeSnapshotWithHttpInfo(response: ResponseContext): Promise<HttpInfo<TakeSnapshotResponse >> {
    if (isCodeInRange("202", response.httpStatusCode)) {
      const body: TakeSnapshotResponse = ObjectSerializer.deserialize(
        await response.body.json(),
        "TakeSnapshotResponse", ""
      ) as TakeSnapshotResponse;
      return new HttpInfo(response.httpStatusCode, response.headers, response.body, body);
    }
    if (isCodeInRange("429", response.httpStatusCode)) {
      throw new ApiException<undefined>(response.httpStatusCode, "Limit exceeded for the number of simultaneous on-demand backup jobs.", undefined, response.headers, response);
    }
    if (isCodeInRange("1XX", response.httpStatusCode)) {
      throw new ApiException<undefined>(response.httpStatusCode, "Informational", undefined, response.headers, response);
    }
    if (isCodeInRange("3XX", response.httpStatusCode)) {
      throw new ApiException<undefined>(response.httpStatusCode, "Redirect", undefined, response.headers, response);
    }
    if (isCodeInRange("4XX", response.httpStatusCode)) {
      const body: Error = ObjectSerializer.deserialize(
        await response.body.json(),
        "Error", ""
      ) as Error;
      throw new ApiException<Error>(response.httpStatusCode, "Client Error", body, response.headers, response);
    }
    if (isCodeInRange("5XX", response.httpStatusCode)) {
      const body: Error = ObjectSerializer.deserialize(
        await response.body.json(),
        "Error", ""
      ) as Error;
      throw new ApiException<Error>(response.httpStatusCode, "Internal Server Error", body, response.headers, response);
    }

    // Work around for missing responses in specification, e.g. for petstore.yaml
    if (response.httpStatusCode < 200 || response.httpStatusCode > 299) {
      throw new ApiException<string | Blob | undefined>(response.httpStatusCode, "Unknown API Status Code!", await response.getBodyAsAny(), response.headers, response);
    }
    const body: TakeSnapshotResponse = await response.body.json() as TakeSnapshotResponse;
    return new HttpInfo(response.httpStatusCode, response.headers, response.body, body);
  }

  /**
   * Unwraps the actual response sent by the server from the response context and deserializes the response content
   * to the expected objects
   *
   * @params response Response returned by the server for a request to takeSnapshot
   * @throws ApiException if the response code was not in [200, 299]
   */
   public async takeSnapshotWithHttpInfo(response: ResponseContext): Promise<HttpInfo<TakeSnapshotResponse >> {
    if (isCodeInRange("202", response.httpStatusCode)) {
      const body: TakeSnapshotResponse = ObjectSerializer.deserialize(
        await response.body.json(),
        "TakeSnapshotResponse", ""
      ) as TakeSnapshotResponse;
      return new HttpInfo(response.httpStatusCode, response.headers, response.body, body);
    }
    if (isCodeInRange("429", response.httpStatusCode)) {
      throw new ApiException<undefined>(response.httpStatusCode, "Limit exceeded for the number of simultaneous on-demand backup jobs.", undefined, response.headers, response);
    }
    if (isCodeInRange("1XX", response.httpStatusCode)) {
      throw new ApiException<undefined>(response.httpStatusCode, "Informational", undefined, response.headers, response);
    }
    if (isCodeInRange("3XX", response.httpStatusCode)) {
      throw new ApiException<undefined>(response.httpStatusCode, "Redirect", undefined, response.headers, response);
    }
    if (isCodeInRange("4XX", response.httpStatusCode)) {
      const body: Error = ObjectSerializer.deserialize(
        await response.body.json(),
        "Error", ""
      ) as Error;
      throw new ApiException<Error>(response.httpStatusCode, "Client Error", body, response.headers, response);
    }
    if (isCodeInRange("5XX", response.httpStatusCode)) {
      const body: Error = ObjectSerializer.deserialize(
        await response.body.json(),
        "Error", ""
      ) as Error;
      throw new ApiException<Error>(response.httpStatusCode, "Internal Server Error", body, response.headers, response);
    }

    // Work around for missing responses in specification, e.g. for petstore.yaml
    if (response.httpStatusCode < 200 || response.httpStatusCode > 299) {
      throw new ApiException<string | Blob | undefined>(response.httpStatusCode, "Unknown API Status Code!", await response.getBodyAsAny(), response.headers, response);
    }
    const body: TakeSnapshotResponse = await response.body.json() as TakeSnapshotResponse;
    return new HttpInfo(response.httpStatusCode, response.headers, response.body, body);
  }

}
